import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingAPI, appointmentAPI, claimAPI, unitAPI, bookingPaymentAPI, developerTemplateAPI } from '../../lib/api';
import PaymentProofDialog from '../../components/common/PaymentProofDialog';
import { formatCurrency, formatDate, formatCommissionRate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import PaymentProofViewer from '../../components/PaymentProofViewer';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Plus, Upload, DollarSign, CheckCircle, Sparkles,
  Landmark, PenLine, ArrowRight, Eye, Receipt, CreditCard, History, Wallet, Mail, Send,
  Download, MapPin, User, Phone, Building2, Calendar, Hash, Loader2, X,
  Link as LinkIcon, Copy, MessageCircle, AlertTriangle, XCircle,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ALL_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'payment_uploaded', label: 'Payment Uploaded' },
  { value: 'payment_verified', label: 'Verified' },
  { value: 'loan_application', label: 'Loan Applied' },
  { value: 'loan_approved', label: 'Loan Approved' },
  { value: 'spa_signed', label: 'SPA Signed' },
  { value: 'completed', label: 'Completed' },
];

const Bookings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Loan dialog
  const [loanDialog, setLoanDialog] = useState(null);
  const [loanForm, setLoanForm] = useState({ status: 'applied', bank_name: '', loan_amount: '', notes: '' });

  // SPA dialog
  const [spaDialog, setSpaDialog] = useState(null);
  // Payment tracking
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [newPayment, setNewPayment] = useState({ amount: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [proofViewer, setProofViewer] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);

  // Loan magic-link dialog (manual share by agent)
  const [loanLinkDialog, setLoanLinkDialog] = useState(null);     // booking_id when open
  const [loanLinkData, setLoanLinkData]   = useState(null);       // {portal_url, token_expires_at, ...}
  const [loanLinkLoading, setLoanLinkLoading] = useState(false);
  const [spaForm, setSpaForm] = useState({ spa_date: '', notes: '' });

  // Upload slip dialog
  const [uploadDialog, setUploadDialog] = useState(null);
  const [paymentSlipFile, setPaymentSlipFile] = useState(null);

  // Booking detail modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [fieldLabelMap, setFieldLabelMap] = useState({});
  const [pdfLoading, setPdfLoading] = useState(null);

  const isAgent = user?.role === 'agent';
  const isAccountOrAdmin = ['account', 'admin', 'super_admin', 'operation_admin'].includes(user?.role);
  const [verifyingPaymentId, setVerifyingPaymentId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const bookingsRes = await bookingAPI.getBookings();
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDepositAmount = (booking) => {
    const amount = booking.booking_fee_amount || booking.deposit_amount || 0;
    return isNaN(amount) ? 0 : amount;
  };

  const handleUploadPaymentSlip = async (bookingId) => {
    if (!paymentSlipFile) { toast.error('Please select a file'); return; }
    try {
      await bookingAPI.uploadPaymentSlip(bookingId, paymentSlipFile);
      toast.success('Payment slip uploaded');
      setUploadDialog(null);
      setPaymentSlipFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload payment slip');
    }
  };

  const handleLoanUpdate = async () => {
    if (!loanDialog) return;
    try {
      await bookingAPI.updateLoanStatus(
        loanDialog,
        loanForm.status,
        loanForm.bank_name,
        parseFloat(loanForm.loan_amount) || 0,
        loanForm.notes
      );
      toast.success(`Loan status updated to ${loanForm.status}`);
      setLoanDialog(null);
      setLoanForm({ status: 'applied', bank_name: '', loan_amount: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update loan status');
    }
  };

  const handleSPAUpdate = async () => {
    if (!spaDialog) return;
    try {
      await bookingAPI.updateSPA(spaDialog, true, spaForm.spa_date, spaForm.notes);
      toast.success('SPA marked as signed!');
      setSpaDialog(null);
      setSpaForm({ spa_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update SPA status');
    }
  };

  const PROOF_REQUIRED_METHODS = ['bank_transfer', 'online_banking', 'cheque'];

  const handleRecordPayment = async () => {
    if (!paymentDialog || !newPayment.amount) return;
    const proofRequired = PROOF_REQUIRED_METHODS.includes(newPayment.payment_method);
    if (proofRequired && !paymentProofFile) {
      toast.error('Please attach a payment proof document for this payment method');
      return;
    }
    try {
      setUploadingProof(true);
      let proofUrl = '';
      let proofName = '';
      if (paymentProofFile) {
        const fd = new FormData();
        fd.append('file', paymentProofFile);
        const upRes = await bookingPaymentAPI.uploadPaymentProof(paymentDialog, fd);
        proofUrl = upRes.data.proof_url;
        proofName = upRes.data.original_name;
      }
      const res = await bookingPaymentAPI.recordPayment(paymentDialog, {
        amount: parseFloat(newPayment.amount),
        payment_method: newPayment.payment_method,
        bank_reference: newPayment.bank_reference,
        notes: newPayment.notes,
        payment_proof: proofUrl,
        payment_proof_name: proofName,
      });
      toast.success(res.data.message);
      setPaymentDialog(null);
      setNewPayment({ amount: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
      setPaymentProofFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setUploadingProof(false);
    }
  };

  const openPaymentHistory = async (bookingId) => {
    try {
      const res = await bookingPaymentAPI.getPayments(bookingId);
      setPaymentHistory(res.data);
      setPaymentHistoryDialog(bookingId);
    } catch (error) {
      toast.error('Failed to load payment history');
    }
  };

  const handleVerifyPaymentRecord = async (paymentId, verified) => {
    if (!paymentHistoryDialog) return;
    setVerifyingPaymentId(paymentId);
    try {
      const res = await bookingPaymentAPI.verifyPaymentRecord(paymentHistoryDialog, paymentId, verified);
      toast.success(res.data.message || (verified ? 'Payment verified' : 'Payment rejected'));
      const refreshed = await bookingPaymentAPI.getPayments(paymentHistoryDialog);
      setPaymentHistory(refreshed.data);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setVerifyingPaymentId(null);
    }
  };

  const downloadPaymentReceipt = async (bookingId, paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/bookings/${bookingId}/payments/${paymentId}/receipt`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
      toast.success('Receipt generated!');
    } catch (error) {
      toast.error('Failed to generate receipt');
    }
  };

  // Booking detail helpers
  const loadFieldLabels = async (booking) => {
    const templateId = booking.developer_template_id;
    if (!templateId) { setFieldLabelMap({}); return; }
    try {
      const res = await developerTemplateAPI.getTemplate(templateId);
      const template = res.data;
      const labelMap = {};
      (template.fields || []).forEach((f, idx) => {
        const fieldId = f.field_id || `ai_field_${f.page || 0}_${idx}`;
        if (f.label) labelMap[fieldId] = f.label;
      });
      setFieldLabelMap(labelMap);
    } catch (err) {
      setFieldLabelMap({});
    }
  };

  const openDetailModal = async (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
    await loadFieldLabels(booking);
  };

  const formatFieldLabel = (key) => {
    if (fieldLabelMap[key]) return fieldLabelMap[key];
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const handleDownloadPdf = async (booking) => {
    setPdfLoading(booking.id);
    try {
      const response = await bookingAPI.downloadPdf(booking.id);
      const { pdf_base64, filename } = response.data;
      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `booking_${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(null);
    }
  };

  const STATUS_LABELS = {
    draft: 'Draft', submitted: 'Submitted', pending_payment: 'Pending Payment', payment_uploaded: 'Payment Uploaded',
    payment_verified: 'Verified', booking_confirmed: 'Booking Confirmed',
    // V4.24.59 — `loan_application` (admin flow) and `loan_applied` (customer
    // questionnaire flow) both render as "Loan Applied" in the history + badge.
    loan_application: 'Loan Applied', loan_applied: 'Loan Applied', loan_approved: 'Loan Approved',
    spa_pending: 'SPA Pending', spa_signed: 'SPA Signed', completed: 'Completed', rejected: 'Rejected',
    cancelled: 'Cancelled', loan_rejected: 'Loan Rejected',
  };

  const STATUS_COLORS = {
    draft: 'bg-gray-100 text-gray-700', submitted: 'bg-slate-100 text-slate-700',
    pending_payment: 'bg-yellow-100 text-yellow-800',
    payment_uploaded: 'bg-blue-100 text-blue-800', payment_verified: 'bg-teal-100 text-teal-800',
    booking_confirmed: 'bg-purple-100 text-purple-800',
    loan_application: 'bg-purple-100 text-purple-800', loan_applied: 'bg-purple-100 text-purple-800',
    loan_approved: 'bg-indigo-100 text-indigo-800',
    spa_pending: 'bg-orange-100 text-orange-800', spa_signed: 'bg-emerald-100 text-emerald-800',
    completed: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700', loan_rejected: 'bg-red-100 text-red-800',
  };

  const filteredBookings = bookings.filter((b) => {
    if (activeTab === 'all') return true;
    return b.status === activeTab;
  });

  const getBookingActions = (booking) => {
    const actions = [];
    const s = booking.status;
    const postPaymentStatuses = ['payment_uploaded', 'payment_verified', 'loan_application', 'loan_approved', 'spa_pending', 'spa_signed', 'completed'];
    const hasPaymentOrLater = postPaymentStatuses.includes(s);

    // Loan magic-link: available across the lifecycle so the agent can
    // share the customer questionnaire any time before SPA-signing.
    const loanLinkStatuses = [
      'draft', 'pending_payment', 'payment_uploaded', 'payment_verified',
      'loan_application', 'loan_approved',
    ];
    if (loanLinkStatuses.includes(s)) {
      actions.push({
        label: 'Loan Link', icon: LinkIcon, variant: 'outline',
        color: 'border-purple-300 text-purple-700 hover:bg-purple-50',
        action: async () => {
          setLoanLinkDialog(booking.id);
          setLoanLinkData(null);
          setLoanLinkLoading(true);
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(
              `${API_BASE}/api/public/v1/bookings/${booking.id}/loan-link`,
              { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
            );
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || 'Failed to generate link');
            setLoanLinkData({ ...(json.data || {}), buyer_phone: booking.buyer_phone || json.data?.buyer_phone });
          } catch (err) {
            toast.error(err.message || 'Failed to generate loan link');
            setLoanLinkDialog(null);
          } finally {
            setLoanLinkLoading(false);
          }
        },
      });
    }

    if (s === 'pending_payment' || s === 'draft') {
      actions.push({ label: 'Upload Slip', icon: Upload, variant: 'outline', action: () => setUploadDialog(booking.id) });
    }
    // Receipt & payment tracking available once payment is uploaded.
    // Note: the booking-level "Download Receipt" button was removed because it
    // showed a different PDF than the per-payment receipts (used legacy
    // `booking_fee_amount` instead of the payments[] array). The "Payment
    // History" dialog now provides the single source of truth — one receipt
    // per recorded payment via downloadPaymentReceipt().
    if (hasPaymentOrLater) {
      // Send Receipt to Buyer via email (available after payment verification)
      const verifiedStatuses = ['payment_verified', 'loan_application', 'loan_approved', 'spa_pending', 'spa_signed', 'completed'];
      if (verifiedStatuses.includes(s)) {
        actions.push({ label: 'Send Receipt', icon: Send, variant: 'outline', color: 'border-blue-300 text-blue-700 hover:bg-blue-50', action: async () => {
          try {
            toast.loading('Sending receipt to buyer...', { id: 'send-receipt' });
            const res = await bookingPaymentAPI.sendReceipt(booking.id);
            toast.success(`Receipt sent to ${res.data.message?.split('to ')[1] || 'buyer'}${res.data.cc?.length ? ` (CC: agent)` : ''}`, { id: 'send-receipt' });
          } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to send receipt';
            toast.error(msg, { id: 'send-receipt' });
          }
        }});
      }
    }
    // Payment tracking — Record Payment stays available whenever there's an
    // outstanding balance, regardless of loan-flow status, until the fee is
    // 100% paid & verified. Payment History is always available once any
    // payment activity exists.
    const totalFee = booking.total_booking_fee || booking.booking_fee_amount || 0;
    const totalPaid = booking.total_paid ?? booking.booking_fee_amount ?? 0;
    const outstanding = Math.max(0, totalFee - totalPaid);
    const terminalForPayments = ['draft', 'cancelled', 'rejected', 'loan_rejected', 'completed', 'spa_signed'];
    const canManagePayments = hasPaymentOrLater || totalPaid > 0
      || ['booking_confirmed', 'processing_loan', 'loan_applied'].includes(s);
    if (canManagePayments) {
      if (outstanding > 0 && !terminalForPayments.includes(s)) {
        actions.push({ label: 'Record Payment', icon: CreditCard, variant: 'outline', color: 'border-amber-300 text-amber-700 hover:bg-amber-50', action: () => setPaymentDialog(booking.id) });
      }
      actions.push({ label: 'Payment History', icon: History, variant: 'outline', color: 'border-gray-300 text-gray-600 hover:bg-gray-50', action: () => openPaymentHistory(booking.id) });
    }
    if (s === 'payment_verified') {
      actions.push({ label: 'Loan Application', icon: Landmark, variant: 'default', color: 'bg-purple-600 hover:bg-purple-700 text-white', action: () => setLoanDialog(booking.id) });
      actions.push({ label: 'Mark SPA Signed', icon: PenLine, variant: 'outline', color: 'border-indigo-300 text-indigo-700 hover:bg-indigo-50', action: () => setSpaDialog(booking.id) });
    }
    if (s === 'loan_application') {
      actions.push({ label: 'Update Loan', icon: Landmark, variant: 'outline', color: 'border-purple-300 text-purple-700 hover:bg-purple-50', action: () => setLoanDialog(booking.id) });
    }
    if (s === 'loan_approved') {
      actions.push({ label: 'Mark SPA Signed', icon: PenLine, variant: 'default', color: 'bg-indigo-600 hover:bg-indigo-700 text-white', action: () => setSpaDialog(booking.id) });
    }
    if (s === 'spa_signed' || s === 'completed') {
      actions.push({ label: 'Claim Commission', icon: DollarSign, variant: 'default', color: 'bg-emerald-600 hover:bg-emerald-700 text-white', action: () => navigate('/commission-claims') });
    }
    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <p className="text-gray-500 mt-1">
            {isAgent ? 'Manage property bookings' : 'View your bookings'}
          </p>
        </div>
        {isAgent && (
          <Link to="/bookings/create">
            <Button className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="new-booking-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </Link>
        )}
      </div>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100/80 flex-wrap h-auto gap-1 p-1">
          {ALL_STATUSES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} className="text-xs sm:text-sm" data-testid={`tab-${s.value}`}>
              {s.label}
              {s.value !== 'all' && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {bookings.filter(b => b.status === s.value).length}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredBookings.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-100">
                      <TableHead className="text-gray-500 font-semibold">Property / Unit</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Buyer</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Booking Fee</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Status</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Date</TableHead>
                      {isAgent && <TableHead className="text-gray-500 font-semibold">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => {
                      const actions = isAgent ? getBookingActions(booking) : [];
                      return (
                        <TableRow key={booking.id} className="border-gray-50 hover:bg-gray-50/50 cursor-pointer" data-testid={`booking-${booking.id}`} onClick={() => openDetailModal(booking)}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{booking.property?.name || '-'}</p>
                              <p className="text-sm text-gray-500">{booking.unit?.unit_number || booking.manual_unit_number || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{booking.buyer_name || '-'}</p>
                              <p className="text-sm text-gray-500">{booking.buyer_phone || booking.buyer_email || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-[#4E50FF] font-mono font-semibold">
                            {formatCurrency(getDepositAmount(booking))}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={booking.status} />
                            {/* Booking Fee Status */}
                            {(booking.total_booking_fee || booking.booking_fee_amount) > 0 && (
                              <div className="mt-1">
                                {(() => {
                                  const totalFee = booking.total_booking_fee || booking.booking_fee_amount || 0;
                                  const totalPaid = booking.total_paid || booking.booking_fee_amount || 0;
                                  const outstanding = Math.max(0, totalFee - totalPaid);
                                  const feeStatus = booking.fee_status || (outstanding <= 0 ? 'fully_paid' : 'partial');
                                  return (
                                    <div className="text-[10px] leading-tight">
                                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium ${feeStatus === 'fully_paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                        <Wallet className="w-2.5 h-2.5" />
                                        {feeStatus === 'fully_paid' ? 'Fully Paid' : `Bal: RM ${outstanding.toLocaleString()}`}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {formatDate(booking.created_at)}
                          </TableCell>
                          {isAgent && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-[#4E50FF] hover:bg-[#4E50FF]/10"
                                  onClick={() => openDetailModal(booking)}
                                  data-testid={`view-detail-${booking.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                                {actions.map((act, idx) => (
                                  <Button
                                    key={idx}
                                    size="sm"
                                    variant={act.variant}
                                    className={act.color || ''}
                                    onClick={act.action}
                                    data-testid={`action-${act.label.toLowerCase().replace(/\s+/g, '-')}-${booking.id}`}
                                  >
                                    <act.icon className="w-3.5 h-3.5 mr-1" />
                                    {act.label}
                                  </Button>
                                ))}
                                {actions.length === 0 && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookings</h3>
              <p className="text-gray-500">
                {activeTab === 'all' ? 'No bookings yet' : `No ${activeTab.replace(/_/g, ' ')} bookings`}
              </p>
              {isAgent && (
                <Link to="/bookings/create">
                  <Button className="mt-4 bg-[#4E50FF] hover:bg-[#3436CC] text-white">
                    <Plus className="w-4 h-4 mr-2" />Create Your First Booking
                  </Button>
                </Link>
              )}
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Payment Slip Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(open) => { if (!open) { setUploadDialog(null); setPaymentSlipFile(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Payment Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setPaymentSlipFile(e.target.files[0])}
              data-testid="upload-slip-input"
            />
            <Button
              onClick={() => handleUploadPaymentSlip(uploadDialog)}
              className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white"
              disabled={!paymentSlipFile}
              data-testid="upload-slip-submit"
            >
              <Upload className="w-4 h-4 mr-2" />Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loan Application Dialog */}
      <Dialog open={!!loanDialog} onOpenChange={(open) => { if (!open) setLoanDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-purple-600" />
              Loan Application Update
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Loan Status *</Label>
              <Select value={loanForm.status} onValueChange={(v) => setLoanForm({ ...loanForm, status: v })}>
                <SelectTrigger data-testid="loan-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="applied">Applied</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input
                value={loanForm.bank_name}
                onChange={(e) => setLoanForm({ ...loanForm, bank_name: e.target.value })}
                placeholder="e.g., Maybank, CIMB, Public Bank"
                data-testid="loan-bank-input"
              />
            </div>
            <div>
              <Label>Loan Amount (RM)</Label>
              <Input
                type="number"
                value={loanForm.loan_amount}
                onChange={(e) => setLoanForm({ ...loanForm, loan_amount: e.target.value })}
                placeholder="e.g., 500000"
                data-testid="loan-amount-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={loanForm.notes}
                onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="loan-notes-input"
              />
            </div>
            <Button
              onClick={handleLoanUpdate}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="loan-submit-btn"
            >
              Update Loan Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SPA Signed Dialog */}
      <Dialog open={!!spaDialog} onOpenChange={(open) => { if (!open) setSpaDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-indigo-600" />
              Mark SPA as Signed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm text-indigo-700">
              This will mark the Sales & Purchase Agreement as signed. Once signed, you can submit your commission claim.
            </div>
            <div>
              <Label>SPA Date</Label>
              <Input
                type="date"
                value={spaForm.spa_date}
                onChange={(e) => setSpaForm({ ...spaForm, spa_date: e.target.value })}
                data-testid="spa-date-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={spaForm.notes}
                onChange={(e) => setSpaForm({ ...spaForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="spa-notes-input"
              />
            </div>
            <Button
              onClick={handleSPAUpdate}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="spa-submit-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm SPA Signed
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(open) => { if (!open) { setPaymentDialog(null); setPaymentProofFile(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Record Booking Fee Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {paymentDialog && (() => {
              const b = bookings.find(b => b.id === paymentDialog);
              const totalFee = b?.total_booking_fee || b?.booking_fee_amount || 0;
              const totalPaid = b?.total_paid || b?.booking_fee_amount || 0;
              const outstanding = Math.max(0, totalFee - totalPaid);
              return (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Total Booking Fee:</span><span className="font-medium">RM {totalFee.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Already Paid:</span><span className="text-emerald-600 font-medium">RM {totalPaid.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-amber-200 pt-1"><span className="text-amber-700 font-medium">Outstanding:</span><span className="text-amber-700 font-bold">RM {outstanding.toLocaleString()}</span></div>
                </div>
              );
            })()}
            <div>
              <Label>Payment Amount (RM) *</Label>
              <Input type="number" step="100" value={newPayment.amount} onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })} placeholder="e.g., 2500" data-testid="record-payment-amount" />
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment({ ...newPayment, payment_method: v })}>
                <SelectTrigger data-testid="record-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="online_banking">Online Banking</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank Reference</Label>
              <Input value={newPayment.bank_reference} onChange={(e) => setNewPayment({ ...newPayment, bank_reference: e.target.value })} placeholder="Transaction reference number" data-testid="record-payment-ref" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newPayment.notes} onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })} placeholder="e.g., Partial payment before SPA" data-testid="record-payment-notes" />
            </div>
            <div>
              <Label>
                Payment Proof {PROOF_REQUIRED_METHODS.includes(newPayment.payment_method) ? '*' : '(optional)'}
              </Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                data-testid="record-payment-proof"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) { setPaymentProofFile(null); return; }
                  const okType = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'].includes(f.type);
                  if (!okType) { toast.error('Unsupported file type. Upload a JPG, PNG or PDF.'); e.target.value = ''; return; }
                  if (f.size > 10 * 1024 * 1024) { toast.error('File too large. Maximum size is 10 MB.'); e.target.value = ''; return; }
                  setPaymentProofFile(f);
                }}
              />
              <p className="text-xs text-gray-400 mt-1">
                {paymentProofFile ? `Attached: ${paymentProofFile.name}` : 'Transaction slip / statement — JPG, PNG or PDF, up to 10 MB'}
              </p>
            </div>
            <Button onClick={handleRecordPayment} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="record-payment-submit" disabled={!newPayment.amount || parseFloat(newPayment.amount) <= 0 || uploadingProof || (PROOF_REQUIRED_METHODS.includes(newPayment.payment_method) && !paymentProofFile)}>
              <CreditCard className="w-4 h-4 mr-2" />{uploadingProof ? 'Uploading…' : 'Record Payment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <PaymentProofDialog
        open={!!proofViewer}
        onClose={() => setProofViewer(null)}
        proofUrl={proofViewer?.url}
        name={proofViewer?.name}
      />

      <Dialog open={!!paymentHistoryDialog} onOpenChange={(open) => { if (!open) { setPaymentHistoryDialog(null); setPaymentHistory(null); }}}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Booking Fee Payment History
            </DialogTitle>
          </DialogHeader>
          {paymentHistory && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total Fee</p>
                  <p className="text-lg font-bold text-gray-900">RM {(paymentHistory.total_booking_fee || 0).toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Verified</p>
                  <p className="text-lg font-bold text-emerald-600">RM {(paymentHistory.total_verified || 0).toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Recorded</p>
                  <p className="text-lg font-bold text-blue-600">RM {(paymentHistory.total_paid || 0).toLocaleString()}</p>
                </div>
                <div className={`rounded-lg p-3 ${(paymentHistory.verified_outstanding ?? paymentHistory.outstanding_balance) > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                  <p className="text-xs text-gray-500">Outstanding</p>
                  <p className={`text-lg font-bold ${(paymentHistory.verified_outstanding ?? paymentHistory.outstanding_balance) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    RM {(paymentHistory.verified_outstanding ?? paymentHistory.outstanding_balance ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
              {(paymentHistory.verified_outstanding ?? paymentHistory.outstanding_balance) > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="payment-outstanding-note">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">
                    Outstanding verified balance of <b>RM {(paymentHistory.verified_outstanding ?? paymentHistory.outstanding_balance ?? 0).toLocaleString()}</b>.
                    {' '}The loan can only be submitted to AiCon after at least one verified payment, and SPA signing requires the full fee to be verified.
                  </p>
                </div>
              )}
              {paymentHistory.payments?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">#</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Amount</TableHead>
                        <TableHead className="text-xs">Method</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Proof</TableHead>
                        <TableHead className="text-xs">Receipt</TableHead>
                        <TableHead className="text-xs">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentHistory.payments.map((p, i) => {
                        const vstatus = p.verification_status || 'verified';
                        const statusStyle = vstatus === 'verified'
                          ? 'bg-emerald-100 text-emerald-800'
                          : vstatus === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-800';
                        const statusLabel = vstatus === 'verified' ? 'Verified' : vstatus === 'rejected' ? 'Rejected' : 'Pending';
                        return (
                        <TableRow key={p.id}>
                          <TableCell className="text-xs font-medium">{i + 1}</TableCell>
                          <TableCell className="text-xs">{p.created_at?.substring(0, 10)}</TableCell>
                          <TableCell className="text-xs font-medium text-emerald-600">RM {p.amount?.toLocaleString()}</TableCell>
                          <TableCell className="text-xs">{(p.payment_method || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusStyle}`} data-testid={`payment-status-${p.id}`}>{statusLabel}</span>
                          </TableCell>
                          <TableCell>
                            {p.payment_proof ? (
                              <button type="button" onClick={() => setProofViewer({ url: p.payment_proof, name: p.payment_proof_name })} className="inline-flex items-center text-xs h-7 text-blue-600 hover:text-blue-800" data-testid={`view-proof-${p.id}`}>
                                <Eye className="w-3 h-3 mr-1" />View
                              </button>
                            ) : (
                              <span className="text-[10px] text-gray-400" data-testid={`no-proof-${p.id}`}>No proof</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-blue-600 hover:text-blue-800" onClick={() => downloadPaymentReceipt(paymentHistoryDialog, p.id)} data-testid={`download-receipt-${p.id}`}>
                              <Receipt className="w-3 h-3 mr-1" />PDF
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {isAccountOrAdmin && vstatus === 'pending' && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50" disabled={verifyingPaymentId === p.id} onClick={() => handleVerifyPaymentRecord(p.id, true)} data-testid={`verify-payment-${p.id}`}>
                                    <CheckCircle className="w-3 h-3 mr-1" />Verify
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-xs h-7 text-red-600 hover:text-red-800 hover:bg-red-50" disabled={verifyingPaymentId === p.id} onClick={() => handleVerifyPaymentRecord(p.id, false)} data-testid={`reject-payment-${p.id}`}>
                                    <XCircle className="w-3 h-3 mr-1" />Reject
                                  </Button>
                                </>
                              )}
                              <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-600 hover:text-emerald-800" data-testid={`send-receipt-${p.id}`} onClick={async () => {
                                try {
                                  toast.loading('Sending...', { id: `send-pr-${p.id}` });
                                  const res = await bookingPaymentAPI.sendPaymentReceipt(paymentHistoryDialog, p.id);
                                  toast.success(`Sent to buyer${res.data.cc?.length ? ' (CC: agent)' : ''}`, { id: `send-pr-${p.id}` });
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to send', { id: `send-pr-${p.id}` });
                                }
                              }}>
                                <Send className="w-3 h-3 mr-1" />Send
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ); })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No payment records found</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#4E50FF]" />
              Booking Details
            </DialogTitle>
          </DialogHeader>

          {selectedBooking && (
            <div className="space-y-5">
              {/* Status & Date */}
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className={`${STATUS_COLORS[selectedBooking.status] || 'bg-gray-100 text-gray-700'} text-sm px-3 py-1`} data-testid="detail-status-badge">
                  {STATUS_LABELS[selectedBooking.status] || selectedBooking.status}
                </Badge>
                <span className="text-sm text-gray-500">
                  <Calendar className="w-3.5 h-3.5 inline mr-1" />
                  Created: {formatDate(selectedBooking.created_at)}
                </span>
                {selectedBooking.spa_signed_at && (
                  <span className="text-sm text-emerald-600">
                    SPA Signed: {formatDate(selectedBooking.spa_signed_at)}
                  </span>
                )}
              </div>

              {/* Property & Unit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Property</p>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedBooking.property?.name || '-'}</p>
                  {selectedBooking.property?.location && (
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />{selectedBooking.property.location}
                    </p>
                  )}
                  {selectedBooking.property?.developer && (
                    <p className="text-xs text-gray-400 mt-1">Developer: {selectedBooking.property.developer}</p>
                  )}
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="w-4 h-4 text-gray-400" />
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Unit</p>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedBooking.unit?.unit_number || selectedBooking.manual_unit_number || '-'}</p>
                  <p className="text-sm text-gray-500">
                    {selectedBooking.unit?.unit_type || '-'} {selectedBooking.unit?.size_sqft ? `• ${selectedBooking.unit.size_sqft} sqft` : ''}
                  </p>
                  {selectedBooking.unit?.price > 0 && (
                    <p className="text-sm font-medium text-[#4E50FF] mt-0.5">{formatCurrency(selectedBooking.unit.price)}</p>
                  )}
                </div>
              </div>

              {/* Buyer Info */}
              <div className="p-4 bg-blue-50/60 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-blue-500" />
                  <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Buyer Information</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  {selectedBooking.buyer_name && (
                    <div><span className="text-gray-500">Name:</span> <span className="font-medium">{selectedBooking.buyer_name}</span></div>
                  )}
                  {selectedBooking.buyer_ic && (
                    <div><span className="text-gray-500">IC/Passport:</span> <span className="font-medium">{selectedBooking.buyer_ic}</span></div>
                  )}
                  {selectedBooking.buyer_phone && (
                    <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{selectedBooking.buyer_phone}</span></div>
                  )}
                  {selectedBooking.buyer_email && (
                    <div><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedBooking.buyer_email}</span></div>
                  )}
                  {selectedBooking.buyer_address && (
                    <div className="sm:col-span-2"><span className="text-gray-500">Address:</span> <span className="font-medium">{selectedBooking.buyer_address}</span></div>
                  )}
                </div>
              </div>

              {/* Financial Summary */}
              <div className="p-4 bg-amber-50/60 rounded-lg border border-amber-100">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="w-4 h-4 text-amber-500" />
                  <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Financial Summary</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Booking Fee</p>
                    <p className="font-semibold text-gray-900">{formatCurrency(selectedBooking.booking_fee_amount || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Total Paid</p>
                    <p className="font-semibold text-emerald-600">{formatCurrency(selectedBooking.total_paid || 0)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Outstanding</p>
                    <p className={`font-semibold ${(selectedBooking.outstanding_balance || 0) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                      {formatCurrency(selectedBooking.outstanding_balance || 0)}
                    </p>
                  </div>
                  {(() => {
                    // Commission Rate display — V4.18.6:
                    // Prefer the booking's commission_snapshot (locked at
                    // creation per V4.10.3) so admin edits to the property's
                    // current rate don't retroactively change historical
                    // bookings. Fall back to the property's live rate when
                    // the booking pre-dates snapshotting.
                    const snap = selectedBooking.commission_snapshot;
                    const source = (snap && (snap.commission_mode || snap.commission_percentage != null || snap.commission_fixed_amount))
                      ? snap
                      : selectedBooking.property;
                    const display = formatCommissionRate(source, { fallback: null });
                    return display ? (
                      <div>
                        <p className="text-gray-500 text-xs">Commission Rate</p>
                        <p className="font-semibold text-[#4E50FF]">{display}</p>
                      </div>
                    ) : null;
                  })()}
                </div>
                {selectedBooking.payment_method && (
                  <p className="text-xs text-gray-500 mt-2">Payment Method: <span className="font-medium">{selectedBooking.payment_method.replace(/_/g, ' ')}</span></p>
                )}
                {selectedBooking.bank_reference && (
                  <p className="text-xs text-gray-500">Bank Ref: <span className="font-medium">{selectedBooking.bank_reference}</span></p>
                )}
              </div>

              {/* Loan Info */}
              {(selectedBooking.loan_bank || selectedBooking.loan_status) && (
                <div className="p-4 bg-purple-50/60 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <Landmark className="w-4 h-4 text-purple-500" />
                    <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Loan Details</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    {selectedBooking.loan_bank && <div><span className="text-gray-500">Bank:</span> <span className="font-medium">{selectedBooking.loan_bank}</span></div>}
                    {selectedBooking.loan_amount > 0 && <div><span className="text-gray-500">Amount:</span> <span className="font-medium">{formatCurrency(selectedBooking.loan_amount)}</span></div>}
                    {selectedBooking.loan_status && <div><span className="text-gray-500">Status:</span> <span className="font-medium capitalize">{selectedBooking.loan_status}</span></div>}
                  </div>
                </div>
              )}

              {/* Payment Slip / Proof */}
              {(selectedBooking.payment_proof || selectedBooking.payment_slip || selectedBooking.payment_proof_url) && (
                <div className="p-4 bg-emerald-50/40 rounded-lg border border-emerald-100">
                  <PaymentProofViewer
                    raw={selectedBooking.payment_proof || selectedBooking.payment_slip || selectedBooking.payment_proof_url}
                    bookingId={selectedBooking.id}
                    label="Payment Slip"
                  />
                </div>
              )}

              {/* Form Data */}
              {selectedBooking.form_data && Object.keys(selectedBooking.form_data).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Booking Form Data</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedBooking.form_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-gray-50 rounded border border-gray-100">
                        <span className="text-gray-500 text-xs">{formatFieldLabel(key)}</span>
                        <span className="font-medium text-right max-w-[60%] truncate text-xs" title={String(value)}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Developer Form Data */}
              {selectedBooking.developer_form_data && Object.keys(selectedBooking.developer_form_data).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Developer Form Data</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedBooking.developer_form_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-purple-50/50 rounded border border-purple-100">
                        <span className="text-gray-500 text-xs">{formatFieldLabel(key)}</span>
                        <span className="font-medium text-right max-w-[60%] truncate text-xs" title={String(value)}>{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              {selectedBooking.status_history?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Status History</h4>
                  <div className="space-y-2">
                    {selectedBooking.status_history.slice().reverse().map((entry, index) => (
                      <div key={index} className="flex items-center gap-3 text-sm">
                        <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(entry.changed_at)}</span>
                        <Badge variant="outline" className="text-xs">{STATUS_LABELS[entry.status] || entry.status}</Badge>
                        {entry.notes && <span className="text-xs text-gray-500">{entry.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions Footer */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                {['submitted', 'payment_uploaded', 'payment_verified', 'loan_application', 'loan_approved', 'spa_signed', 'completed'].includes(selectedBooking.status) && (
                  <Button
                    onClick={() => handleDownloadPdf(selectedBooking)}
                    disabled={pdfLoading === selectedBooking.id}
                    className="flex-1 bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                    data-testid="detail-download-pdf"
                  >
                    {pdfLoading === selectedBooking.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1"
                  data-testid="detail-close-btn"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Loan Magic-Link dialog — agent copies & shares manually */}
      <Dialog open={!!loanLinkDialog} onOpenChange={(open) => { if (!open) { setLoanLinkDialog(null); setLoanLinkData(null); } }}>
        <DialogContent className="max-w-lg" data-testid="loan-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-purple-600" />
              Loan Questionnaire Link
            </DialogTitle>
          </DialogHeader>
          {loanLinkLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-gray-600">Generating link…</span>
            </div>
          )}
          {loanLinkData && !loanLinkLoading && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Share this link with your customer to complete the loan questionnaire.
                The link expires on <span className="font-semibold">{formatDate(loanLinkData.token_expires_at)}</span>.
              </p>

              <div>
                <Label className="text-xs text-gray-500 uppercase">Magic Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={loanLinkData.portal_url || ''}
                    readOnly
                    className="font-mono text-xs"
                    data-testid="loan-link-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="loan-link-copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(loanLinkData.portal_url || '');
                      toast.success('Link copied to clipboard');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="loan-link-whatsapp-btn"
                  onClick={() => {
                    const phone = (loanLinkData.buyer_phone || '').replace(/\D/g, '');
                    const msg = `Hi${loanLinkData.buyer_name ? ' ' + loanLinkData.buyer_name : ''}, please complete your loan questionnaire here: ${loanLinkData.portal_url}\n\nLink expires ${formatDate(loanLinkData.token_expires_at)}.`;
                    const waUrl = phone
                      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
                      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
                    window.open(waUrl, '_blank');
                  }}
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="loan-link-email-btn"
                  onClick={() => {
                    const subject = 'Complete Your Loan Profile — SAM Rumah';
                    const body = `Hi${loanLinkData.buyer_name ? ' ' + loanLinkData.buyer_name : ''},\n\nPlease complete your loan questionnaire here:\n${loanLinkData.portal_url}\n\nThis link expires on ${formatDate(loanLinkData.token_expires_at)}.\n\nRegards,\nSAM Rumah`;
                    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>

              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                <span className="font-semibold">Note:</span> Each time you open this dialog a fresh link is generated and any
                previous link is invalidated. Share the latest copy with the customer.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookings;
